import { initialize, submit, change, registerField } from 'redux-form';
import {
  success,
  loading,
  error,
  set,
  reset,
  handleErrorException,
} from 'State/actions';
import {
  RecipeFieldEnum as Fields,
  IngredientFieldEnum as IngredientFields,
} from 'Common/FieldEnum';
import {
  EDIT_RECIPE_FORM_NAME,
  EDIT_INGREDIENT_FORM_NAME,
} from 'Common/FormEnum';
import { isRequestCancelation } from 'Common/Helpers';

import * as types from './types';
import {
  convertMNU_RecipeToRecipe,
  convertRecipeToMNU_Recipe,
  updateNutritionFacts,
  cleanUpRecipeValues,
  mergeRecipeAttributes,
} from '../../helpers/recipe';

export const resetDeleteError = () =>
  reset(types.MANAGE_RECIPE_DELETE_RECIPE_ERROR, null);

export const resetDuplicateError = () =>
  reset(types.MANAGE_RECIPE_DUPLICATE_RECIPE_ERROR, null);

export const setDeleteSuccessful = (payload) =>
  set(types.MANAGE_RECIPE_SET_DELETE_SUCCESSFUL, payload);

export const setDuplicateSuccessful = (payload) =>
  set(types.MANAGE_RECIPE_SET_DUPLICATE_SUCCESSFUL, payload);

export const resetInactiveError = () =>
  reset(types.MANAGE_RECIPE_WRITE_INACTIVE_ERROR, null);

export const setInactiveSuccessful = (payload) =>
  set(types.MANAGE_RECIPE_SET_INACTIVE_SUCCESSFUL, payload);

export const deleteRecipe =
  (recipeId) => async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_RECIPE_DELETE_RECIPE_LOADING, true));
      await MPWWebApi.postGenericInsertUpdate(`Recipe/Delete/${recipeId}`);
      dispatch(success(types.MANAGE_RECIPE_DELETE_RECIPE_SUCCESS, recipeId));
      dispatch(setDeleteSuccessful(true));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.MANAGE_RECIPE_DELETE_RECIPE_ERROR, {
          Title: 'Error',
          Message: 'Unable to delete recipe.',
        })
      );
    } finally {
      dispatch(loading(types.MANAGE_RECIPE_DELETE_RECIPE_LOADING, false));
    }
  };

export const duplicateRecipe =
  (recipeId) => async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_RECIPE_DUPLICATE_RECIPE_LOADING, true));
      const response = await MPWWebApi.postGenericInsertUpdate(
        `Recipe/Duplicate/${recipeId}`
      );
      const duplicatedRecipeId = response.data.Data.duplicatedRecipeId;
      dispatch(setDuplicateSuccessful(duplicatedRecipeId));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.MANAGE_RECIPE_DUPLICATE_RECIPE_ERROR, {
          Title: 'Error',
          Message: 'Unable to duplicate recipe.',
        })
      );
    } finally {
      dispatch(loading(types.MANAGE_RECIPE_DUPLICATE_RECIPE_LOADING, false));
    }
  };

export const writeRecipeInactive =
  (recipeId, inactive = true) =>
  async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_RECIPE_WRITE_INACTIVE_LOADING, true));
      const data = { RecipeId: recipeId, Inactive: inactive };
      await MPWWebApi.postGenericInsertUpdate('Recipe/WriteInactive', data);
      dispatch(
        success(types.MANAGE_RECIPE_WRITE_INACTIVE_SUCCESS, {
          recipeId,
          inactive,
        })
      );
      dispatch(setInactiveSuccessful(true));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.MANAGE_RECIPE_DELETE_RECIPE_ERROR, {
          Title: 'Error',
          Message: 'Unable to delete recipe.',
        })
      );
    } finally {
      dispatch(loading(types.MANAGE_RECIPE_WRITE_INACTIVE_LOADING, false));
    }
  };

export const getRecipe = (recipeId) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_RECIPE_FETCHING, true));

      const response = await MPWWebApi.getGeneric(
        `Recipe/GetRecipeDetail/${recipeId}`
      );

      const data = response.data.Data;
      const result = cleanUpRecipeValues(data);

      dispatch(loading(types.MANAGE_RECIPE_FETCHING, false));

      const recipe = convertMNU_RecipeToRecipe(result);
      dispatch(initialize(EDIT_RECIPE_FORM_NAME, recipe));
      dispatch(success(types.MANAGE_RECIPE_FETCH_SUCCESS, recipe));
    } catch (ex) {
      dispatch(loading(types.MANAGE_RECIPE_FETCHING, false));
      dispatch(error(types.MANAGE_RECIPE_FETCH_ERROR, ex));
    }
  };
};

export const saveRecipe = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    let result = false;
    let recipe = data;
    dispatch(loading(types.MANAGE_RECIPE_FETCHING, true));

    try {
      const MNU_Recipe = convertRecipeToMNU_Recipe(data);
      // recipe details
      const response = await MPWWebApi.postGenericInsertUpdate(
        `Recipe/Write/`,
        MNU_Recipe
      );
      recipe = response.data.Data;

      // recipe picture
      if (data[Fields.PicturePreview]) {
        const formData = new FormData();
        formData.append('file', data[Fields.PicturePreview]);
        const pictureResult = await MPWWebApi.postGenericFile(
          `recipe/picture/${recipe[Fields.Id]}`,
          formData,
          null,
          false
        );

        recipe.Picture = pictureResult.data.Data;
      }

      result = recipe.Id;

      dispatch(() => ({
        type: types.MANAGE_RECIPE_SAVE_NEW_RECIPE_SUCCESS,
        payload: recipe.Id,
      }));

      dispatch(setSaveSuccessful(true));
    } catch (ex) {
      if (ex.response.status === 409) {
        dispatch(
          handleErrorException(ex, types.MANAGE_RECIPE_FETCH_INFO, {
            Title: 'Error',
            Message: 'Could not save recipe.',
          })
        );
      } else {
        dispatch(
          handleErrorException(ex, types.MANAGE_RECIPE_FETCH_ERROR, {
            Title: 'Error',
            Message: 'Could not save recipe.',
          })
        );
      }
    } finally {
      dispatch(loading(types.MANAGE_RECIPE_FETCHING, false));

      recipe = cleanUpRecipeValues(recipe);
      recipe = convertMNU_RecipeToRecipe(recipe);

      dispatch(success(types.MANAGE_RECIPE_FETCH_SUCCESS, recipe));
      dispatch(initialize(EDIT_RECIPE_FORM_NAME, recipe));

      return result;
    }
  };
};

export const getRecipeNutritionFacts = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const { Api, source } = MPWWebApi.createCancelableApi();

      dispatch(
        loading(
          types.MANAGE_RECIPE_GET_NUTRITION_FACTS_FETCHING,
          true,
          true,
          source
        )
      );

      const MNU_Recipe = convertRecipeToMNU_Recipe(data);

      const payload = {
        Recipe: {
          ...MNU_Recipe,
          [Fields.Picture]: null,
        },
        RecipeId: MNU_Recipe[Fields.Id],
        [Fields.Ingredients]: MNU_Recipe[Fields.Ingredients],
        [Fields.TypeOfFat]: MNU_Recipe[Fields.TypeOfFat],
        [Fields.MoistureChange]: MNU_Recipe[Fields.MoistureChange],
        [Fields.FatChange]: MNU_Recipe[Fields.FatChange],
        [Fields.NumberOfPortion]: MNU_Recipe[Fields.NumberOfPortion],
      };

      const response = await Api.postGenericInsertUpdate(
        `Recipe/GetRecipeNutritionFacts`,
        payload
      );

      const result = response.data.Data;
      const parsedResult = cleanUpRecipeValues(result);

      const recipe = updateNutritionFacts(data, parsedResult);

      dispatch(
        registerField(EDIT_RECIPE_FORM_NAME, Fields.ComponentType, 'Field')
      );
      dispatch(initialize(EDIT_RECIPE_FORM_NAME, recipe, true));
      dispatch(
        change(
          EDIT_RECIPE_FORM_NAME,
          Fields.Allergens,
          recipe[Fields.Allergens]
        )
      );

      const currentAttributes = data[Fields.Attributes] || [];
      const attributesFromAPI = recipe[Fields.Attributes] || [];

      const mergedAttributes = mergeRecipeAttributes(
        currentAttributes,
        attributesFromAPI
      );

      dispatch(
        change(EDIT_RECIPE_FORM_NAME, Fields.Attributes, mergedAttributes)
      );
      dispatch(
        change(
          EDIT_RECIPE_FORM_NAME,
          Fields.ComponentType,
          recipe[Fields.ComponentType]
        )
      );
      dispatch(
        loading(types.MANAGE_RECIPE_GET_NUTRITION_FACTS_FETCHING, false)
      );
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.MANAGE_RECIPE_GET_NUTRITION_FACTS_ERROR,
          {
            Title: 'Error',
            Message: 'Could not load nutrition facts.',
          }
        )
      );

      if (!isRequestCancelation(ex)) {
        dispatch(
          loading(types.MANAGE_RECIPE_GET_NUTRITION_FACTS_FETCHING, false)
        );
      }
    }
  };
};

export const clearErrors = () => {
  return {
    type: types.MANAGE_RECIPE_CLEAR_ERRORS,
  };
};

export const clearInfo = () => {
  return {
    type: types.MANAGE_RECIPE_CLEAR_INFO,
  };
};

export const submitRecipeForm = () => submit(EDIT_RECIPE_FORM_NAME);

export const convertRecipeToIngredient = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.MANAGE_RECIPE_FETCHING, true));

    try {
      const recipe = convertRecipeToMNU_Recipe(data);
      const result = await MPWWebApi.postGenericInsertUpdate(
        'Recipe/WriteConvertRecipeToIngredient',
        recipe
      );
      const ingredient = result.data.Data;
      dispatch(
        change(
          EDIT_INGREDIENT_FORM_NAME,
          IngredientFields.Id,
          ingredient[IngredientFields.Id]
        )
      );
      dispatch(
        change(
          EDIT_RECIPE_FORM_NAME,
          Fields.ConvertedToIngredientId,
          ingredient[IngredientFields.Id]
        )
      );
      dispatch(
        change(
          EDIT_RECIPE_FORM_NAME,
          Fields.ConvertedToIngredientCNCode,
          ingredient[IngredientFields.CN_Code]
        )
      );
      dispatch(
        change(
          EDIT_RECIPE_FORM_NAME,
          Fields.ConvertedToIngredientDescription,
          ingredient[IngredientFields.Description]
        )
      );
      dispatch(
        success(types.MANAGE_RECIPE_SET_CONVERT_TO_INGREDIENT_SUCCESSFUL, true)
      );
    } catch (ex) {
      dispatch(handleErrorException(ex, types.MANAGE_RECIPE_FETCH_ERROR), {
        Title: 'Error',
        Message: 'Could not convert recipe to ingredient',
      });
    } finally {
      dispatch(loading(types.MANAGE_RECIPE_FETCHING, false));
    }
  };
};

export const setConvertToIngredientSuccessful = (success) => ({
  type: types.MANAGE_RECIPE_SET_CONVERT_TO_INGREDIENT_SUCCESSFUL,
  payload: success,
});

export const setSaveSuccessful = (success) =>
  set(types.MANAGE_RECIPE_SET_SAVE_SUCCESSFUL, success);

export const setShowIngredientModal = (show) =>
  set(types.MANAGE_RECIPE_SET_SHOW_INGREDIENT_MODAL, show);



// WEBPACK FOOTER //
// ./src/pages/managerecipe/state/editrecipe/actions.js